import { useEffect, useRef } from "react";
import type { TrackPublication } from "livekit-client";
import { audioContext, outputNode } from "@shared/media";

/**
 * Картинка дорожки LiveKit.
 *
 * Дорожка сама вставляет себя в элемент и сама из него убирается — React
 * содержимое этого элемента не трогает, иначе они бы спорили за него.
 */
export function TrackView({
  publication,
  className,
  muted,
}: {
  publication: TrackPublication;
  className?: string;
  /** Своё видео слушать не нужно: получится эхо. */
  muted?: boolean;
}) {
  const holder = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = holder.current;
    const track = publication.track;
    if (!element || !track) return;

    track.attach(element);
    return () => {
      track.detach(element);
    };
  }, [publication, publication.track]);

  return <video ref={holder} className={className} autoPlay playsInline muted={muted} />;
}

/**
 * Звук участника: голос или трансляция.
 *
 * Держится отдельным элементом без картинки: голос должен звучать и тогда,
 * когда плашка человека прокручена за край экрана.
 *
 * Слышно его не через сам элемент, а через Web Audio: у элемента громкость
 * ограничена единицей, а прибавлять надо и выше — у показывающего игра бывает
 * убавлена почти в ноль. Элемент при этом остаётся и остаётся приглушённым:
 * без него браузеры не пускают поток от собеседника в обработку звука, а без
 * приглушения он играет то же самое вторым голосом (см. `hush` ниже).
 */
export function AudioView({
  publication,
  volume = 1,
}: {
  publication: TrackPublication;
  /** Единица — как есть. Больше — прибавить, вплоть до `MAX_VOLUME`. */
  volume?: number;
}) {
  const holder = useRef<HTMLAudioElement>(null);
  const gain = useRef<GainNode | null>(null);

  useEffect(() => {
    const element = holder.current;
    const track = publication.track;
    const source = track?.mediaStreamTrack;

    if (!element || !track || !source) return;

    track.attach(element);

    /*
     * Приглушить **после** подключения, а не разметкой, — и держать так.
     *
     * `muted` в разметке не работает: `attachToElement` у `livekit-client`
     * ставит `element.muted = mediaStream.getAudioTracks().length === 0`,
     * то есть на звуковой дорожке снимает приглушение сам. Своё он оставляет
     * только при `webAudioMix`, когда сам же и мешает звук через Web Audio; у
     * нас его нет, потому что мешаем мы.
     *
     * А без приглушения голос звучит дважды: из элемента напрямую и из нашего
     * графа, с расхождением на задержку вывода звукового узла. Это десятки
     * миллисекунд — не эхо, а гребёнка: голос жужжит и тарахтит, и за каждым
     * звуком идёт короткий призвук. Слышно это только в разговоре, поэтому
     * подозрение и падало по очереди на шумодав, микрофон и сеть.
     *
     * Слушатель на `volumechange` — от `startAudio`: он снимает приглушение со
     * всех подключённых элементов при том же `webAudioMix: false`. Возврат
     * события на себя не зациклится: то же значение его не поднимает.
     */
    const hush = () => {
      element.muted = true;
      element.volume = 0;
    };

    hush();
    element.addEventListener("volumechange", hush);

    const context = audioContext();
    const input = context.createMediaStreamSource(new MediaStream([source]));
    const node = context.createGain();

    input.connect(node);
    // Через общий узел, а не прямо в выход: там живёт общая громкость.
    node.connect(outputNode());
    gain.current = node;

    return () => {
      element.removeEventListener("volumechange", hush);
      input.disconnect();
      node.disconnect();
      gain.current = null;
      track.detach(element);
    };
  }, [publication, publication.track]);

  // Отдельно от подключения: громкость меняют часто, а пересобирать из-за
  // этого всю цепочку значило бы прерывать звук на каждом движении ползунка.
  useEffect(() => {
    if (gain.current) gain.current.gain.value = volume;
  }, [volume]);

  return <audio ref={holder} autoPlay muted />;
}
